// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { unwrap } from "@granite/api-client";
import { Avatar, Button, Icon, Spinner } from "@granite/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type SubmitEvent } from "react";
import { useStore } from "zustand";

import { AppShell, Group, Page, PageHeader, Section } from "../components/AppShell.js";
import { FormAlert } from "../components/AuthLayout.js";
import { NotesArt } from "../components/Illustration.js";
import { VaultMenu } from "../components/VaultMenu.js";
import { api } from "../lib/api.js";
import { errorMessage } from "../lib/errors.js";
import { formatDate, t } from "../lib/i18n.js";
import { hasUser, sessionStore } from "../lib/session.js";
import { listVaults, type Vault } from "../lib/vaults.js";

/** Signed-in home: the vaults and workspaces, in the app shell. */
export function HomePage() {
  const session = useStore(sessionStore);
  const vaults = useQuery({
    queryKey: ["vaults"],
    queryFn: listVaults,
  });

  useEffect(() => {
    document.title = `${t("home.title")} · ${t("app.name")}`;
  }, []);

  if (!hasUser(session)) return null;
  return (
    <AppShell>
      <Page wide>
        <PageHeader title={t("home.title")} description={t("home.signedInAs", { email: session.user.email })} />
        <Section title={t("home.vaultsHeading")} description={t("home.vaultHint")}>
          {vaults.isPending ? (
            <p role="status" className="flex items-center gap-2 text-sm text-fg-muted">
              <Spinner size="sm" />
              {t("common.loading")}
            </p>
          ) : vaults.isError ? (
            <FormAlert tone="error">{errorMessage(vaults.error)}</FormAlert>
          ) : (
            <>
              {vaults.data.offline && <FormAlert tone="info">{t("home.offline")}</FormAlert>}
              <Group>
                {vaults.data.items.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 px-4 pt-8 pb-7 text-center text-[13px] text-fg-muted">
                    <NotesArt />
                    <p className="max-w-xs">{t("home.empty")}</p>
                  </div>
                ) : (
                  <VaultList vaults={vaults.data.items} />
                )}
                <CreateVault />
              </Group>
            </>
          )}
        </Section>
        <Workspaces />
      </Page>
    </AppShell>
  );
}

function VaultList({ vaults }: { vaults: Vault[] }) {
  return (
    <ul className="divide-y divide-border">
      {vaults.map((v) => (
        <li key={v.id}>
          <VaultMenu vault={v}>
            <Link
              to="/vault/$vaultId/$"
              params={{ vaultId: v.id }}
              search={{}}
              className="group flex h-12 items-center gap-3 px-4 transition-colors outline-offset-[-2px] hover:bg-surface-muted/60"
            >
              <span className="flex size-7 items-center justify-center rounded-md border border-border bg-bg text-fg-muted group-hover:text-fg">
                <Icon name="vault" size={15} />
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg">{v.name}</span>
              <span className="hidden text-xs text-fg-muted sm:block">{t("home.updated", { date: formatDate(v.updated_at) })}</span>
              <Icon name="chevronRight" size={14} className="text-fg-muted opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          </VaultMenu>
        </li>
      ))}
    </ul>
  );
}

function CreateVault() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/v1/vaults", { body: { name: name.trim() } })),
    onSuccess: async (vault) => {
      await queryClient.invalidateQueries({ queryKey: ["vaults"] });
      await navigate({
        to: "/vault/$vaultId/$",
        params: { vaultId: vault.id },
        search: {},
      });
    },
  });
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };
  return (
    <form onSubmit={submit} aria-label={t("home.createTitle")} className="flex flex-col gap-1 bg-bg px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Icon name="plus" size={15} className="ml-1 shrink-0 text-fg-muted" />
        <input
          aria-label={t("home.vaultName")}
          placeholder={t("home.vaultName")}
          className="g-input g-input-bare h-8 flex-1 bg-transparent"
          value={name}
          maxLength={100}
          aria-invalid={create.error ? true : undefined}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <Button type="submit" size="sm" loading={create.isPending} disabled={!name.trim()}>
          {t("home.createVault")}
        </Button>
      </div>
      {create.error && <p role="alert" className="px-1 text-xs text-danger">{errorMessage(create.error)}</p>}
    </form>
  );
}

/** The company workspace the person belongs to (PLAN.md P8-T01). Only company (Business) accounts have one; others see nothing. */
function Workspaces() {
  const mine = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => unwrap(await api.GET("/v1/workspaces")),
  });
  if (!mine.data || mine.data.items.length === 0) return null;
  return (
    <Section id="workspaces-title" title={t("home.workspaces")} description={t("home.workspacesHint")}>
      <Group>
        <ul aria-label={t("home.yourWorkspaces")} className="divide-y divide-border">
          {mine.data.items.map((w) => (
            <li key={w.id}>
              <Link
                to="/w/$workspaceId"
                params={{ workspaceId: w.id }}
                className="group flex h-12 items-center gap-3 px-4 text-[13px] font-medium text-fg transition-colors outline-offset-[-2px] hover:bg-surface-muted/60"
              >
                <Avatar name={w.name} size={22} />
                <span className="min-w-0 flex-1 truncate">{w.name}</span>
                <span className="rounded-md border border-border px-1.5 py-0.5 text-[11px] font-medium text-fg-muted capitalize">{w.role}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Group>
    </Section>
  );
}
